import { useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';
import { flatten } from 'lodash';
import * as api from '../../services/api';
import { HELP_DESK_DEFAULT_VIEW, HELP_DESK_DEFAULT_STATUS, HELP_DESK_DEFAULT_SORT } from '../../constants/helpDesk';

const PAGE_SIZE = 20;

export const HELP_DESK_TICKETS_QUERY_KEY = 'helpDeskTickets';

const getHelpDeskTicketsQueryKey = (
  accountId,
  source,
  { view, status, isResolvedFolder, search, sort, categoryIds, assignedAdminIds, priorities, branchIds }
) => [
  HELP_DESK_TICKETS_QUERY_KEY,
  source,
  accountId,
  view,
  status,
  isResolvedFolder,
  search,
  sort,
  categoryIds,
  assignedAdminIds,
  priorities,
  branchIds
];

const useHelpDeskTickets = ({
  accountId,
  source = 'main',
  view = HELP_DESK_DEFAULT_VIEW,
  status = HELP_DESK_DEFAULT_STATUS,
  search,
  sort = HELP_DESK_DEFAULT_SORT,
  categoryIds,
  assignedAdminIds,
  priorities,
  branchIds,
  enabled = true
}) => {
  const queryParams = useMemo(
    () => ({
      view,
      status: status === 'RESOLVED' ? 'OPEN' : status,
      isResolvedFolder: status === 'RESOLVED' ? true : undefined,
      search: search?.trim() || undefined,
      sort,
      categoryIds,
      assignedAdminIds,
      priorities,
      branchIds
    }),
    [view, status, search, sort, categoryIds, assignedAdminIds, priorities, branchIds]
  );

  const queryKey = useMemo(() => getHelpDeskTicketsQueryKey(accountId, source, queryParams), [
    accountId,
    source,
    queryParams
  ]);

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage, isError, error } = useInfiniteQuery(
    queryKey,
    ({ pageParam = 0 }) =>
      api.fetchHelpDeskTickets({ accountId, ...queryParams, limit: PAGE_SIZE, offset: pageParam * PAGE_SIZE }),
    {
      enabled: !!accountId && enabled,
      getNextPageParam: (lastPage, allPages) => {
        const total = lastPage?.total ?? 0;
        const fetched = allPages.reduce((sum, p) => sum + (p?.data?.length ?? 0), 0);
        return fetched < total ? allPages.length : undefined;
      }
    }
  );

  const tickets = useMemo(() => flatten((data?.pages ?? []).map((p) => p?.data ?? [])), [data?.pages]);
  const firstPage = data?.pages?.[0];
  const tabCounts = useMemo(() => firstPage?.tabCounts ?? {}, [firstPage]);
  const total = firstPage?.total;

  return {
    tickets,
    tabCounts,
    total,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isError,
    error,
    pageSize: PAGE_SIZE
  };
};

export default useHelpDeskTickets;
export { getHelpDeskTicketsQueryKey, PAGE_SIZE };
